import React, { useEffect, useMemo, useState } from 'react';
import { useGetChatTaskToolsSchemas } from 'react-scale/hooks/chatTaskTools/useGetChatTaskToolSchemas';
import { z } from 'zod';
import _ from 'lodash';
import { ZodFormBuilder } from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ZodFormBuilder';
import JSONInputBuilder from '../../../../../../../corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import { Checkbox } from '@scale/scaleui';

import TwoTextSxSDiffView from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/TwoTextSxSDiffView';

interface ToolInputEditorProps {
  toolId: string;
  onChange: (value: any) => void;
  value: object | string;
  originalValue: object | string;
}

const jsonToZod = (schema: any): z.ZodTypeAny => {
  if (typeof schema !== 'object' || schema === null) {
    throw new Error('Invalid schema');
  }

  switch (schema.type) {
    case 'string':
      return z.string();
    case 'number':
      return z.number();
    case 'integer':
      return z.number().int();
    case 'boolean':
      return z.boolean();
    case 'null':
      return z.null();
    case 'array':
      if (schema.items) {
        return z.array(jsonToZod(schema.items));
      }
      return z.array(z.any());
    case 'object':
      if (schema.properties) {
        const shape: { [key: string]: z.ZodTypeAny } = {};
        for (const [key, value] of Object.entries(schema.properties)) {
          shape[key] = jsonToZod(value);
          if (
            (schema.required && Array.isArray(schema.required) && !schema.required.includes(key)) ||
            _.isEmpty(schema.required)
          ) {
            shape[key] = shape[key].optional();
          }
        }
        let zodObject = z.object(shape);
        if (schema.required && Array.isArray(schema.required)) {
          zodObject = zodObject.required(schema.required);
        }
        return zodObject;
      }
      return z.object({});
    default:
      return z.any().transform(val => ({
        _def: { shape: () => ({}) },
        ...val,
      }));
  }
};

const ToolInputEditor: React.FC<ToolInputEditorProps> = ({
  toolId,
  onChange,
  value,
  originalValue,
}) => {
  const { data: toolSchemas, isLoading } = useGetChatTaskToolsSchemas({ toolIds: [toolId] });
  const [jsonMode, setJsonMode] = useState(false);
  const [failedZodSchema, setFailedZodSchema] = useState(false);

  const zodSchema = useMemo(() => {
    if (!toolSchemas) return null;
    return jsonToZod(toolSchemas?.toolDefinitions?.[toolId]?.inputSchema);
  }, [toolSchemas, toolId]);

  useEffect(() => {
    const badFormat = !!zodSchema && !zodSchema.safeParse(value).success;
    setFailedZodSchema(badFormat);
  }, [value, zodSchema]);

  const forceShowJsonEditor =
    !zodSchema ||
    typeof value !== 'object' ||
    !toolId ||
    Object.keys(zodSchema._def.shape()).length === 0 ||
    failedZodSchema;

  return (
    <div className="flex flex-col gap-2 w-full border border-neutral-200 rounded-lg p-4">
      {failedZodSchema && (
        <div className="text-red-500">
          The input parameters do not match the expected schema. Please edit the raw JSON.
        </div>
      )}
      {!forceShowJsonEditor && (
        <Checkbox
          checked={jsonMode}
          onChange={() => setJsonMode(!jsonMode)}
          label={'Edit as raw JSON'}
        />
      )}

      {forceShowJsonEditor || jsonMode ? (
        <JSONInputBuilder
          name={'Parameters for tool call'}
          existingValue={value}
          updateProperty={onChange}
        />
      ) : isLoading ? (
        <div>Loading schema for tool `{toolId}`...</div>
      ) : (
        <>
          {Object.keys(zodSchema._def.shape()).map(key => {
            const subSchema = zodSchema._def.shape()[key];

            return (
              <div className="flex flex-col gap-2" key={key}>
                {/* @ts-ignore */}
                {originalValue?.[key] && (
                  <TwoTextSxSDiffView
                    // @ts-ignore
                    textOne={originalValue?.[key]?.toString() ?? ''}
                    // @ts-ignore
                    textTwo={value?.[key]?.toString() ?? ''}
                    textOneTitle={`Original: ${key}`}
                    textTwoTitle={`Rewrite: ${key}`}
                    variant="stretch"
                  />
                )}
                <ZodFormBuilder
                  key={key}
                  label={key}
                  schema={subSchema}
                  // @ts-ignore
                  value={value?.[key]}
                  onChange={(newValue: any) => {
                    const newFullJson = {
                      ...value,
                      [key]: newValue,
                    };
                    // delete any keys that are not in the schema
                    Object.keys(newFullJson).forEach(k => {
                      if (!Object.keys(zodSchema._def.shape()).includes(k)) {
                        delete newFullJson[k];
                      }
                    });
                    onChange(newFullJson);
                  }}
                  startOpen={true}
                />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ToolInputEditor;
